import * as React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Button,
  TextField,
  Theme,
  Select,
  MenuItem,
  FormHelperText,
  InputLabel,
  FormControl,
  Dialog,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SitesInput from '../utils/components/SitesInput';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%',
    padding: theme.spacing(5),
  },
  formItem: {
    minHeight: 120,
    [theme.breakpoints.down('lg')]: {
      minHeight: 'unset',
    },
  },
  button: {
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  chip: {
    margin: theme.spacing(0.5, 0.5, 0.5, 0),
  },
}));

type Props = {
  onSubmit: (data: UserFormDto) => void;
  onCancel?: () => void;
  cancelButton?: boolean;
  defaultValues?: UserFormDto;
  buttonText: string;
  loading: boolean;
  open?: boolean;
};

const UserForm: React.FC<Props> = ({
  onSubmit,
  onCancel = () => null,
  defaultValues = {
    name: '',
    email: '',
    role: 'STOCKING',
    locale: 'FI',
    siteIds: [],
  },
  buttonText,
  cancelButton = false,
  loading,
  open,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, control } = useForm<UserFormDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const requiredMessage = t('validation:t.required');
  const emailMessage = t('validation:t.email');

  const formContent = (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid size={{ lg: 4, xs: 12}} className={classes.formItem}>
          <Controller
            name="name"
            control={control}
            rules={{ required: requiredMessage }}
            render={({ field }) => (
              <TextField
                {...field}
                autoComplete="off"
                fullWidth
                variant="outlined"
                color="secondary"
                margin="normal"
                id="name"
                label={t('users:t.username')}
                autoFocus
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ lg: 4, xs: 12}} className={classes.formItem}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: requiredMessage,
              pattern: {
                value: /^[^@]+@[^@]+\.[^@]+$/,
                message: emailMessage,
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoComplete="off"
                fullWidth
                variant="outlined"
                color="secondary"
                margin="normal"
                id="email"
                label={t('users:t.email')}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </Grid>
        <Grid size={{ lg: 4, xs: 12}} className={classes.formItem}>
          <FormControl
            variant="outlined"
            error={!!errors.role}
            margin="normal"
            fullWidth
            style={{ display: 'block' }}
          >
            <InputLabel id="role-label">{t('users:t.role')}</InputLabel>
            <Controller
              render={({ field }) => (
                <Select
                  {...field}
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  id="role"
                  labelId="role-label"
                  label={t('users:t.role')}
                >
                  <MenuItem value="ADMIN">{t('users:t.admin')}</MenuItem>
                  <MenuItem value="STOCKING">{t('users:t.stocking')}</MenuItem>
                </Select>
              )}
              rules={{ required: requiredMessage }}
              name="role"
              control={control}
              defaultValue="STOCKING"
            />
            <FormHelperText>{errors.role?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={{ lg: 4, xs: 12}} className={classes.formItem}>
          <FormControl
            variant="outlined"
            error={!!errors.locale}
            margin="normal"
            fullWidth
            style={{ display: 'block' }}
          >
            <InputLabel id="locale-label">{t('users:t.locale')}</InputLabel>
            <Controller
              render={({ field }) => (
                <Select
                  {...field}
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  id="locale"
                  labelId="locale-label"
                  label={t('users:t.locale')}
                >
                  <MenuItem value="FI">FI</MenuItem>
                  <MenuItem value="EN">EN</MenuItem>
                </Select>
              )}
              rules={{ required: requiredMessage }}
              name="locale"
              control={control}
            />
            <FormHelperText>{errors.locale?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={{ lg:12, xs: 12}}>
          <Controller
            name="siteIds"
            control={control}
            render={({ field: { onChange, value } }) => (
              <SitesInput value={value ?? []} onChange={onChange} />
            )}
            defaultValue={[]}
          />
        </Grid>
        <Grid size={{lg: 6, xs: 10}} container direction="row">
          {cancelButton && (
            <Button
              variant="contained"
              color="secondary"
              classes={{ root: classes.button }}
              onClick={onCancel}
            >
              {t('common:t.cancel')}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            classes={{ root: classes.button }}
            disabled={loading}
          >
            {buttonText}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  if (open !== undefined) {
    return (
      <Dialog open={open} onClose={onCancel}>
        {formContent}
      </Dialog>
    );
  }

  return formContent;
};

export default UserForm;
